import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Facebook, Youtube } from "lucide-react";
import { useTheme } from "@/hooks/use-theme";
import { resolveAssetUrl, ENDPOINTS, withTrailingSlash } from "@/config";
import logoDark from "@/assets/logo_dark.png";
import logoLight from "@/assets/logo_light.webp";

const PAYMENT_IMAGE = resolveAssetUrl("uploads/home-sections/Payment.webp");

const QUICK_LINKS = [
  { label: "About Us", href: withTrailingSlash("/about") },
  { label: "Contact Us", href: withTrailingSlash("/contact") },
  { label: "Blog", href: withTrailingSlash("/blog") },
  { label: "Bulk Order Inquiry", href: withTrailingSlash("/bulk-orders") },
  { label: "Store Locator", href: withTrailingSlash("/store-locator") },
  { label: "Track Your Order", href: withTrailingSlash("/track-your-order") },
];

const CUSTOMER_SERVICE_LINKS = [
  { label: "FAQ", href: withTrailingSlash("/faq") },
  { label: "Privacy Policy", href: withTrailingSlash("/privacy-policy-2") },
  { label: "Returns & Refunds", href: withTrailingSlash("/returns-refunds-policy") },
  { label: "Warranty Policy", href: withTrailingSlash("/service-center-warranty-claims-policy") },
  { label: "Delivery Policy", href: withTrailingSlash("/delivery-policy") },
];

const DEFAULT_SOCIAL = {
  facebook: "https://www.facebook.com/fonexpress.ea",
  twitter: "https://x.com/fonexpress",
  whatsapp: "https://wa.me/254788740000",
  instagram: "https://www.instagram.com/fonexpress/",
  youtube: "https://www.youtube.com/channel/UC8DS4S7FOj02VV3P2stkx6A",
};

const SOCIAL_ITEMS = [
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "twitter", label: "X", Icon: Twitter },
  { key: "whatsapp", label: "WhatsApp", Icon: null },
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "youtube", label: "YouTube", Icon: Youtube },
];

export function Footer() {
  const { theme } = useTheme();
  const [subscribed, setSubscribed] = useState(false);
  const [socialLinks, setSocialLinks] = useState(DEFAULT_SOCIAL);

  useEffect(() => {
    const loadSocial = async () => {
      try {
        const res = await fetch(ENDPOINTS.SOCIAL_MEDIA.PUBLIC);
        const data = await res.json();
        if (data?.isOk && data.data) {
          setSocialLinks((prev) => ({ ...prev, ...data.data }));
        }
      } catch (error) {
        console.error("Failed to load social media links:", error);
      }
    };
    loadSocial();
  }, []);

  return (
    <footer className="border-t border-border bg-surface/40">
      <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-10">
        <div className="flex flex-col lg:flex-row lg:items-start gap-8 xl:gap-10">
          <div className="flex-1 min-w-0 space-y-3">
            <div className="p-3 rounded-lg border border-border bg-card w-full max-w-lg">
              <p className="text-center text-sm font-bold text-foreground mb-2 tracking-wide">
                Guaranteed SAFE Checkout
              </p>
              <img
                src={PAYMENT_IMAGE}
                alt="M-Pesa, Airtel Money, Visa, Mastercard, Amex"
                className="w-full h-auto object-contain"
                loading="lazy"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
              <div>
                <h4 className="font-display font-bold text-foreground mb-1">Head Office</h4>
                <p className="text-sm text-muted-foreground leading-snug">
                  7th Floor, The Oval Westlands, Ring Road Parklands, Nairobi
                  <br />
                  Head Office Contacts - +254 788 740000 | +254 722 740000
                </p>
              </div>

              <div>
                <h4 className="font-display font-bold text-foreground mb-1">Service Center</h4>
                <p className="text-sm text-muted-foreground leading-snug">
                  Ground Floor, Amee Arcade Parklands Road Westlands, Nairobi, Kenya
                  <br />
                  OR Ground Floor, Kenya Cinema Plaza, along Moi Avenue, Nairobi
                  <br />
                  Call us on: +254 720 628 628
                  <br />
                  Email:{" "}
                  <a href="mailto:scadmin@fonexpress.net" className="hover:text-foreground">
                    scadmin@fonexpress.net
                  </a>{" "}
                  or{" "}
                  <a href="mailto:carecenter@fonexpress.net" className="hover:text-foreground">
                    carecenter@fonexpress.net
                  </a>
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-2">Connect With Us Below..</p>
              <div className="flex flex-nowrap items-center gap-2">
                {SOCIAL_ITEMS.map(({ key, label, Icon }) => {
                  const href = socialLinks[key]?.trim();
                  if (!href) return null;

                  return (
                    <a
                      key={key}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="w-9 h-9 shrink-0 rounded-lg bg-surface border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                    >
                      {key === "whatsapp" ? (
                        <i className="ri-whatsapp-line text-base"></i>
                      ) : Icon ? (
                        <Icon className="h-4 w-4" />
                      ) : null}
                    </a>
                  );
                })}
              </div>

              <div className="mt-4">
                <img
                  src={theme === "dark" ? logoDark : logoLight}
                  alt="Fonexpress"
                  className="h-9 w-auto"
                />
                <p className="mt-2 text-sm text-muted-foreground leading-snug max-w-md">
                  Maneno Yote. Kenya&apos;s go-to destination for smartphones, laptops, audio &amp; gaming.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-nowrap items-start gap-x-8 lg:gap-x-10 gap-y-6 w-full sm:w-auto shrink-0">
            <Col title="Quick Links" links={QUICK_LINKS} />
            <Col title="Customer Service" links={CUSTOMER_SERVICE_LINKS} />

            <div className="w-full sm:w-[240px] shrink-0">
              <h4 className="font-display font-bold mb-2">Stay Charged</h4>
              <p className="text-sm text-muted-foreground mb-2 leading-snug">
                Drops, deals, and early access — straight to your inbox.
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setSubscribed(true);
                }}
                className="flex gap-2"
              >
                <input
                  type="email"
                  required
                  placeholder="you@email.com"
                  className="flex-1 min-w-0 bg-background border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  type="submit"
                  className="px-4 py-2.5 rounded-lg bg-gradient-brand text-primary-foreground text-sm font-semibold hover:shadow-glow-cyan transition-shadow shrink-0"
                >
                  {subscribed ? "✓" : "Join"}
                </button>
              </form>
              {subscribed && (
                <p className="mt-2 text-xs text-success animate-fade-in">
                  You&apos;re in! Welcome to the crew.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="container mx-auto px-4 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} Fonexpress Kenya. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <Link to={withTrailingSlash("/terms-and-conditions")} className="hover:text-foreground">
              Terms
            </Link>
            <Link to={withTrailingSlash("/privacy-policy-2")} className="hover:text-foreground">
              Privacy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function Col({ title, links }: { title: string; links: { label: string; href: string }[] }) {
  return (
    <div className="shrink-0">
      <h4 className="font-display font-bold mb-2">{title}</h4>
      <ul className="space-y-1.5 text-sm leading-tight">
        {links.map((l) => (
          <li key={l.label}>
            <Link
              to={l.href}
              className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
